import { randomChars } from "@/lib/random-string";

export class AiPromptFieldError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export const PROMPT_DEFAULT_GROUP_ID = "default";

export const SYSTEM_RULES_CODE = "systemRules";
export const TITLE_RULES_CODE = "titleRules";
export const TRANSLATE_RULES_CODE = "translateRules";
export const TAG_RULES_CODE = "tagRules";

export function isPromptDefaultGroupId(id: string) {
  return id === PROMPT_DEFAULT_GROUP_ID;
}

export const PROMPT_STATUSES = ["active", "archived"] as const;
export type PromptStatus = (typeof PROMPT_STATUSES)[number];

export const PROMPT_STATUS_LABELS: Record<PromptStatus, string> = {
  active: "在用",
  archived: "已归档",
};

export const PROMPT_USE_MODES = ["private", "org", "platform"] as const;
export type PromptUseMode = (typeof PROMPT_USE_MODES)[number];

export const PROMPT_USE_MODE_LABELS: Record<PromptUseMode, string> = {
  private: "不开放（仅管理者）",
  org: "企业内可用",
  platform: "全站用户可用",
};

export function isPromptUseMode(value: unknown): value is PromptUseMode {
  return (
    typeof value === "string" &&
    PROMPT_USE_MODES.includes(value as PromptUseMode)
  );
}

export const PROMPT_LIST_MODES = ["none", "listed"] as const;
export type PromptListMode = (typeof PROMPT_LIST_MODES)[number];

export function isPromptListMode(value: unknown): value is PromptListMode {
  return (
    typeof value === "string" &&
    PROMPT_LIST_MODES.includes(value as PromptListMode)
  );
}

export const PROMPT_REVIEW_STATUSES = [
  "none",
  "pending",
  "approved",
  "rejected",
] as const;
export type PromptReviewStatus = (typeof PROMPT_REVIEW_STATUSES)[number];

export const PROMPT_REVIEW_STATUS_LABELS: Record<PromptReviewStatus, string> = {
  none: "无需审核",
  pending: "待审核",
  approved: "已通过",
  rejected: "已驳回",
};

export function isPromptPubliclyListed(input: {
  listMode: string;
  reviewStatus: string;
}): boolean {
  if (input.listMode !== "listed") {
    return false;
  }

  return input.reviewStatus === "none" || input.reviewStatus === "approved";
}

export const PROMPT_PURCHASE_TYPES = ["none", "long_term"] as const;
export type PromptPurchaseType = (typeof PROMPT_PURCHASE_TYPES)[number];

export const PROMPT_PURCHASE_TYPE_LABELS: Record<PromptPurchaseType, string> = {
  none: "免费",
  long_term: "长期使用",
};

export function isPromptPurchaseType(
  value: unknown,
): value is PromptPurchaseType {
  return (
    typeof value === "string" &&
    PROMPT_PURCHASE_TYPES.includes(value as PromptPurchaseType)
  );
}

export function resolvePromptPurchaseType(
  listMode: PromptListMode,
  raw?: string | null,
): PromptPurchaseType {
  if (listMode !== "listed") {
    return "none";
  }

  return isPromptPurchaseType(raw) ? raw : "none";
}

const PROMPT_CODE_ALPHABET =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const PROMPT_CODE_LENGTH = 12;
export const PROMPT_CODE_MAX_LENGTH = 32;

export function createPromptCode(): string {
  return randomChars(PROMPT_CODE_LENGTH, PROMPT_CODE_ALPHABET);
}

export function sanitizePromptCode(raw: string): string {
  return raw.replace(/[^0-9A-Za-z]/g, "").slice(0, PROMPT_CODE_MAX_LENGTH);
}

export const PROMPT_NAME_MAX = 20;
export const PROMPT_DESCRIPTION_MAX = 150;

export const PROMPT_TAG_MAX = 12;
export const PROMPT_TAGS_MAX_COUNT = 5;

export function normalizePromptTags(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of raw) {
    if (typeof item !== "string") {
      continue;
    }

    const text = item.trim().slice(0, PROMPT_TAG_MAX);

    if (!text || seen.has(text)) {
      continue;
    }

    seen.add(text);
    result.push(text);

    if (result.length >= PROMPT_TAGS_MAX_COUNT) {
      break;
    }
  }

  return result;
}

export function normalizePromptPrice(
  raw: number | undefined,
  options?: { maxPrice?: number },
): number {
  if (raw === undefined) {
    return 0;
  }

  if (!Number.isInteger(raw) || raw < 0) {
    throw new AiPromptFieldError("VALIDATION_ERROR", "积分价格须为非负整数");
  }

  const maxPrice = options?.maxPrice ?? 0;

  if (maxPrice > 0 && raw > maxPrice) {
    throw new AiPromptFieldError(
      "VALIDATION_ERROR",
      `积分价格不得超过 ${maxPrice}`,
    );
  }

  return raw;
}

const PROMPT_VARIABLE_PATTERN = /\{\{\s*([0-9A-Za-z_\u4e00-\u9fa5]+)\s*\}\}/g;

export const PROMPT_VARIABLE_KEY_MAX = 32;

export const PROMPT_VARIABLES_MAX_COUNT = 20;

export function extractPromptVariableKeys(body: string): string[] {
  if (typeof body !== "string" || !body) {
    return [];
  }

  const seen = new Set<string>();
  const keys: string[] = [];

  for (const match of body.matchAll(PROMPT_VARIABLE_PATTERN)) {
    const key = match[1].slice(0, PROMPT_VARIABLE_KEY_MAX);

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    keys.push(key);

    if (keys.length >= PROMPT_VARIABLES_MAX_COUNT) {
      break;
    }
  }

  return keys;
}

export function readPromptVariables(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of raw) {
    const source =
      typeof item === "string"
        ? item
        : item && typeof item === "object"
          ? (item as Record<string, unknown>).key
          : null;

    if (typeof source !== "string") {
      continue;
    }

    const key = source.trim().slice(0, PROMPT_VARIABLE_KEY_MAX);

    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(key);

    if (result.length >= PROMPT_VARIABLES_MAX_COUNT) {
      break;
    }
  }

  return result;
}

const PROMPT_FILL_SEPARATOR = "：";

export function buildPromptScaffold(keys: string[]): string {
  return keys.map((key) => `{{${key}}}${PROMPT_FILL_SEPARATOR}`).join("\n");
}

export function composePromptMessage(
  body: string,
  keys: string[],
  input: string,
): string {
  const declared = new Set(keys);
  const filled = input
    .split("\n")
    .filter((line) => {
      const blank = /^\s*(.+?)\s*[:：]\s*$/.exec(line);

      if (!blank) {
        return true;
      }

      return !declared.has(blank[1].replace(/^\{\{|\}\}$/g, "").trim());
    })
    .join("\n")
    .trim();

  const base = body.trim();

  if (!base) {
    return filled;
  }

  return filled ? `${base}\n\n${filled}` : base;
}

export function renderPromptBody(
  body: string,
  keys: string[],
  values: Record<string, string>,
): string {
  if (typeof body !== "string" || !body) {
    return "";
  }

  const declared = new Set(keys);

  return body.replaceAll(PROMPT_VARIABLE_PATTERN, (whole, rawKey: string) => {
    const key = rawKey.slice(0, PROMPT_VARIABLE_KEY_MAX);

    if (!declared.has(key)) {
      return whole;
    }

    return (values[key] ?? "").trim();
  });
}
